import { useQuery, useMutation } from '@tanstack/react-query';
import { API_URL } from '../../config';
import { toApiError } from '../../lib/api-error';
import type { TaskRepo } from '../tasks/hooks/useTaskRepos';
import type { AgentTaskTriggerType } from '../tasks/types';

interface TaskReposResponse {
  ok: boolean;
  data: TaskRepo[];
}

export type FeedbackFetch = (
  input: Parameters<typeof fetch>[0],
  init?: Parameters<typeof fetch>[1]
) => ReturnType<typeof fetch>;

type FetchFeedbackReposOptions = {
  apiUrl?: string;
  fetchFn?: FeedbackFetch;
};

export async function fetchFeedbackRepos({
  apiUrl = API_URL,
  fetchFn = fetch,
}: FetchFeedbackReposOptions = {}): Promise<TaskReposResponse> {
  const res = await fetchFn(`${apiUrl}/superadmin/feedback/repos`, {
    credentials: 'include',
  });
  if (!res.ok) throw await toApiError(res, 'Failed to fetch feedback repos');
  return res.json() as Promise<TaskReposResponse>;
}

/** Fetch repos from the internal org (superadmin-only). */
export function useFeedbackRepos({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ['feedback-repos'],
    queryFn: () => fetchFeedbackRepos(),
    enabled,
    staleTime: 5 * 60_000,
  });
}

interface FeedbackScreenshot {
  storageKey: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
}

interface SubmitFeedbackPayload {
  propertyGithubRepoId: string;
  title: string;
  description: string;
  triggerType: AgentTaskTriggerType;
  tags?: string[];
  priority?: number;
  screenshot?: FeedbackScreenshot;
}

interface UploadScreenshotResponse {
  ok: boolean;
  data?: FeedbackScreenshot;
  error?: string;
}

/**
 * Upload a bug-report screenshot to S3 via the dedicated feedback endpoint.
 * Intentionally separate from the org Attachments domain — feedback
 * screenshots must not surface in the user's attachments library.
 */
export function useUploadFeedbackScreenshot() {
  return useMutation({
    mutationKey: ['feedback-screenshot-upload'],
    mutationFn: async (file: File): Promise<UploadScreenshotResponse> => {
      const form = new FormData();
      form.append('file', file);
      const res = await fetch(`${API_URL}/superadmin/feedback/screenshot`, {
        method: 'POST',
        credentials: 'include',
        body: form,
      });
      if (!res.ok) throw await toApiError(res, 'Failed to upload feedback screenshot');
      return res.json();
    },
  });
}

export function feedbackScreenshotRawUrl(storageKey: string): string {
  return `${API_URL}/superadmin/feedback/screenshot/raw?key=${encodeURIComponent(storageKey)}`;
}

/** Create a feedback task in the internal org (superadmin-only). */
export function useSubmitFeedback() {
  return useMutation({
    mutationKey: ['feedback-submit'],
    mutationFn: async (payload: SubmitFeedbackPayload) => {
      const res = await fetch(`${API_URL}/superadmin/feedback`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw await toApiError(res, 'Failed to submit feedback');
      return res.json();
    },
  });
}
